import { useContext } from 'react'
import { useParams } from 'react-router'
import { type StoreConfigurationScopedRouteParams } from '../../legacy/common/types'
import { NativeScopeContext } from './nativeScopeContext'

/**
 * The sole eager reader of scope route params. Every scope hook
 * (`usePartnerId`, `useRetailerId(s)`, `useStoreConfigId`) and the scope-picker
 * gate derive from this, so the RRv5-vs-TanStack read lives in exactly one place.
 *
 * Returns the same shape RRv5's `useParams<StoreConfigurationScopedRouteParams>()`
 * did — a given route only carries the scopes it declares, but the type mirrors the
 * long-standing convention so the derived hooks keep their exact signatures.
 *
 * Always reads the native-scope context and RRv5 params unconditionally. The context
 * defaults to non-native when no TanStack shell is mounted, so legacy routes keep using
 * RRv5 params; native routes use the parameters supplied by `NativeScopeProvider`.
 */
// Exported for tests and native scope-provider integration.

export const useScopeParamsShellAware = (): StoreConfigurationScopedRouteParams => {
  const native = useContext(NativeScopeContext)
  // RRv5 read stays unconditional (rules of hooks). The shell's root layout mounts
  // a legacy <Router> above every route — native and catch-all — so this read
  // always has a router context (empty params on a native route, real ones on the
  // legacy tree). See `tanstackShell` RootLayout.
  const legacy = useParams<StoreConfigurationScopedRouteParams>()

  if (!native.isNative) return legacy

  // On a native route the params come from TanStack (via context); legacy is the
  // per-key fallback so the return keeps its all-string shape.
  return {
    partner_id: native.params.partner_id ?? legacy.partner_id,
    warehouse_id: native.params.warehouse_id ?? legacy.warehouse_id,
    storeConfigId: native.params.storeConfigId ?? legacy.storeConfigId,
  }
}

export const useScopeParams = useScopeParamsShellAware
